import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  styled,
  useTheme,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import Avatar from "@mui/material/Avatar";
import { Button, paletteVar, Typography } from "@platform/design-system";

import {
  formatBytes,
  validateFileName,
  validateFileSize,
  sanitizeFileNameLength,
  updateFileName,
  validateDuplicateFileName,
} from "../utils";
import { submitEvent } from "../utils/tracking";

import UploadFileError from "./uploadFileError";

const DropZoneBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.fontFamily,
  overflow: "hidden",
  position: "relative",
}));

const DropZoneFilesBox = styled(Box)(({ theme }) => ({
  maxHeight: theme.spacing(44),
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const FileBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
}));

const FileInfo = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  display: "flex",
  flex: "1",
  flexDirection: "column",
}));

export const RenderFile = ({
  file,
  icon,
  onFileDownload = () => {},
  avatarIcon,
  ...rest
}) => {
  const theme = useTheme();
  return (
    <FileBox
      {...rest}
      elevation={1}
      sx={{
        marginY: theme.spacing(0.5),
      }}
    >
      <Avatar sx={{ bgcolor: paletteVar("surfaces-secondary", "#FAF0FA") }}>
        {avatarIcon ?? <></>}
      </Avatar>
      <FileInfo onClick={() => !file.loading && onFileDownload(file)}>
        <Typography variant="body-large" sx={{ wordBreak: "break-all" }}>
          {file.displayName || file.name}
        </Typography>
        <Typography variant="body-medium" color="textSecondary">
          <strong>
            {!file.loading && `${formatBytes(file.size || file.contentLength)}`}
          </strong>
        </Typography>
        {file.loading && <LinearProgress sx={{ marginTop: 1 }} />}
      </FileInfo>
      {!file.loading && (icon ?? <></>)}
    </FileBox>
  );
};

const notifyRejection = (
  fileRejected,
  filesAlreadyNotified,
  setFilesAlreadyNotified,
) => {
  if (
    !filesAlreadyNotified.find(
      (currFile) => currFile === fileRejected.file.name,
    )
  ) {
    submitEvent("RFP Upload Error", {
      error: fileRejected.errors[0].code,
      fileName: fileRejected.file.name,
    });
    setFilesAlreadyNotified([...filesAlreadyNotified, fileRejected.file.name]);
  }
};

const RenderFileRejected = ({
  fileRejected,
  maxSize,
  allowedFormatLabel,
  onRemove,
}) => {
  const { errors, file } = fileRejected;
  const error = errors[0];
  const [filesAlreadyNotified, setFilesAlreadyNotified] = useState([]);
  const [errorViewedAlreadyNotified, setErrorViewedAlreadyNotified] = useState(
    [],
  );

  const typesUploadErrors = {
    "file-invalid-type": {
      message: ` File extension is not supported (allowed ${allowedFormatLabel})`,
    },
    duplicate: {
      message:
        "You have uploaded a duplicate file. Please make sure you are uploading the correct file or rename the file and try again.",
    },
    "current-documentType-duplicate": {
      message: (
        <>
          {" "}
          The file{" "}
          <Typography variant="caption" component="span" color="inherit">
            <strong>{file.name}</strong>
          </Typography>{" "}
          has already been uploaded to this Document Type.
        </>
      ),
    },
    "other-documentType-duplicate": {
      message: (
        <>
          {" "}
          The file{" "}
          <Typography variant="caption" component="span" color="inherit">
            <strong>{file.name}</strong>
          </Typography>{" "}
          {}
          already exists. You can still add it to this Document Type by clicking
          on &quot;Select From Uploaded Documents&quot;.
        </>
      ),
    },
    "additional-exist-in-other-documentType": {
      message: (
        <>
          {" "}
          The file{" "}
          <Typography variant="caption" component="span" color="inherit">
            <strong>{file.name}</strong>
          </Typography>{" "}
          cannot be uploaded to Additional Documents because it has already been
          uploaded to another Document Type.
        </>
      ),
    },
    oversize: {
      message: `File size exceeds the maximum (${maxSize}MB).`,
    },
    "too-many-files": {
      message: (
        <>
          {" "}
          <Typography component="span" color="inherit">
            <strong>Uploading multiple files is not supported.</strong>
          </Typography>{" "}
          Please upload, assign, and save one file at a time.
        </>
      ),
    },
    "invalid-characters": {
      message: `File names cannot contain special characters ("*/?<>:|).`,
    },
  };

  const errorReason = typesUploadErrors[error.code]?.message;

  const errorKey = `${file.name}_${error.code}`;
  useEffect(() => {
    notifyRejection(
      fileRejected,
      filesAlreadyNotified,
      setFilesAlreadyNotified,
    );
  }, [fileRejected, filesAlreadyNotified]);
  useEffect(() => {
    if (!errorViewedAlreadyNotified.includes(errorKey)) {
      submitEvent("Error Viewed", {
        error_type: "file_rejected",
        error_reason: errorReason,
      });
      setErrorViewedAlreadyNotified([...errorViewedAlreadyNotified, errorKey]);
    }
  }, [errorViewedAlreadyNotified, errorKey, errorReason]);

  return (
    <UploadFileError
      fileName={file.name}
      error={errorReason}
      onClose={() => onRemove(file)}
      code={error.code}
    />
  );
};

const CustomDropzone = ({
  files,
  handleUploadFiles,
  maxSize,
  allowDuplicated = false,
  handleValidation = () => {},
  acceptedFormatFiles,
  allowedFormatLabel,
  isMultiple,
  isAllowToManage,
  isLoading = false,
  uploadedFiles,
  setOpenUploadedDocs,
  isAdditionalDocument = false,
  documentTypeName,
}) => {
  const theme = useTheme();
  const [rejectedFiles, setRejectedFiles] = useState([]);

  // Read through a ref so the upload effect below doesn't depend on the
  // handler's identity: callers pass inline functions, and re-running the
  // effect on every render would re-upload the same accepted files.
  const handleUploadFilesRef = useRef(handleUploadFiles);
  useEffect(() => {
    handleUploadFilesRef.current = handleUploadFiles;
  });

  const handleDefaultValidation = ({ name, size }) => {
    handleValidation({ name, size });
    try {
      validateFileSize(size, maxSize);
    } catch {
      return {
        code: "oversize",
      };
    }

    try {
      if (!allowDuplicated) {
        validateDuplicateFileName(name, documentTypeName, uploadedFiles, files);
      }
    } catch (err) {
      return {
        code: err.message,
      };
    }

    try {
      validateFileName(name);
    } catch {
      return {
        code: "invalid-characters",
      };
    }
  };

  const { acceptedFiles, fileRejections, getRootProps, getInputProps } =
    useDropzone({
      accept: acceptedFormatFiles,
      validator: handleDefaultValidation,
      multiple: isMultiple,
    });

  useEffect(() => {
    const rejections = [...fileRejections];
    if (acceptedFiles.length > 0) {
      const filesAfterNameModified = acceptedFiles.map((file) => {
        const updatedFileName = sanitizeFileNameLength(file.name);

        return updateFileName(file, updatedFileName);
      });
      handleUploadFilesRef.current([...filesAfterNameModified]);
    }

    const tooManyRejection = rejections.find(
      (r) => r.errors[0].code === "too-many-files",
    );
    if (tooManyRejection) {
      setRejectedFiles([tooManyRejection]);
    } else {
      setRejectedFiles(rejections);
    }
  }, [acceptedFiles, fileRejections, maxSize]);

  const handleRemoveRejected = (file) => {
    const newFiles = rejectedFiles.filter(
      (currFile) => currFile.file.name !== file.name,
    );
    setRejectedFiles(newFiles);
  };
  return (
    <>
      {rejectedFiles.length ? (
        <DropZoneFilesBox
          mt={2}
          sx={{
            maxHeight: theme.spacing(44),
            overflow: "auto",
          }}
        >
          {rejectedFiles.map((fileRejected) => (
            <RenderFileRejected
              key={`${fileRejected.file?.name || "unknown"}:${fileRejected.errors?.[0]?.code || "err"}`}
              maxSize={maxSize}
              fileRejected={fileRejected}
              allowedFormatLabel={allowedFormatLabel}
              onRemove={handleRemoveRejected}
            />
          ))}
        </DropZoneFilesBox>
      ) : null}

      <StyledBox>
        <Box>
          {isLoading ? (
            <Grid textAlign="center" mt={2}>
              <CircularProgress size={20} />
            </Grid>
          ) : (
            isAllowToManage && (
              <DropZoneBox {...getRootProps({ mt: theme.spacing(3) })}>
                <input {...getInputProps()} />
                <Button
                  variant="primary"
                  size="sm"
                  sx={{ padding: "4px 40px", fontWeight: "600" }}
                >
                  Upload New Documents
                </Button>
                <Box marginTop="8px">
                  <StyledItalicTypography color="textSecondary">
                    Drag and drop files up to 40MB,
                  </StyledItalicTypography>
                  <StyledItalicTypography color="textSecondary">
                    or use the button above&mdash;
                  </StyledItalicTypography>
                </Box>
              </DropZoneBox>
            )
          )}
        </Box>
        {!isAdditionalDocument && (
          <>
            <Typography
              sx={{ margin: "0 5px", paddingBottom: "25px" }}
              variant="body-medium"
            >
              <strong>OR</strong>
            </Typography>
            <Box>
              <Button
                variant="primary"
                size="sm"
                sx={{ fontWeight: "600" }}
                disabled={uploadedFiles.length === 0}
                onClick={() => setOpenUploadedDocs(true)}
              >
                Select from Uploaded Documents
              </Button>

              <StyledLabel color="textSecondary">
                Reuse previously uploaded files
              </StyledLabel>
            </Box>
          </>
        )}
      </StyledBox>
    </>
  );
};

const StyledItalicTypography = styled(Typography)(() => ({
  display: "flex",
  fontStyle: "italic",
  justifyContent: "center",
  fontSize: "0.875rem",
}));

const StyledLabel = styled(Typography)(() => ({
  display: "flex",
  marginTop: "8px",
  fontStyle: "italic",
  fontSize: "0.875rem",
  justifyContent: "center",
}));

const StyledBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 160,
  border: "1px dashed rgba(224, 224, 224, 1)",
  gap: 2,
}));

export default CustomDropzone;
